import React, { useContext, useMemo, useState } from "react"
import { NavLink, Redirect } from "react-router-dom"
import firebase from "firebase"
import moment from "moment"
import { auth, db } from "../../utils/firebase"
import { AlertsContext } from "../../utils/providers/alerts"
import { AuthContext } from "../../utils/providers/auth"
import { isSuperAdminAllowlisted } from "../../utils/exports/super-admin-allowlist"
import "./stableford-transfer.scss"

// UI components
import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Input from "../../components/ui/inputs/input"
import Button from "../../components/ui/button/button"
import Table from "../../components/structure/table/table"
import WindowCard from "../../components/ui/window-card/window-card"
import Badge from "../../components/ui/badge/badge"

const formatCurrency = (value) => {
	return new Intl.NumberFormat("en-GB", {
		style: "currency",
		currency: "GBP",
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(value)
}

const hashString = (input) => {
	let hash = 0
	for (let i = 0; i < input.length; i++) {
		hash = (hash << 5) - hash + input.charCodeAt(i)
		hash |= 0
	}
	return `v1_${input.length}_${Math.abs(hash)}`
}

const appendBookingNote = (existingNotes, appendText) => {
	const existing = typeof existingNotes === "string" ? existingNotes : ""
	const spacer = existing.length === 0 ? "" : existing.endsWith("\n") ? "\n" : "\n\n"
	return `${existing}${spacer}${appendText}`
}

const isValidNumberReference = (value) => {
	return value !== "" && !Number.isNaN(Number(value))
}

const dedupeBookingDocs = (documents) => {
	const map = new Map()
	documents.forEach((doc) => {
		map.set(doc.id, doc)
	})
	return [...map.values()]
}

const getTravelStartMillis = (bookingData) => {
	// Prefer booking check_in, then fall back to date_from fields for older/migrated docs.
	const bookingCheckIn = bookingData?.check_in
	if (bookingCheckIn?.seconds) return bookingCheckIn.seconds * 1000
	if (typeof bookingCheckIn === "number" && Number.isFinite(bookingCheckIn)) return bookingCheckIn

	const bookingDateFrom = bookingData?.date_from
	if (bookingDateFrom?.seconds) return bookingDateFrom.seconds * 1000
	if (typeof bookingDateFrom === "number" && Number.isFinite(bookingDateFrom)) return bookingDateFrom

	const enquiryDateFrom = bookingData?.enquiry?.date_from
	if (enquiryDateFrom?.seconds) return enquiryDateFrom.seconds * 1000
	if (typeof enquiryDateFrom === "number" && Number.isFinite(enquiryDateFrom)) return enquiryDateFrom

	return null
}

const findSubsetForTarget = (bookings, targetPoints) => {
	const sums = new Map()
	sums.set(0, [])

	bookings.forEach((booking, index) => {
		const entries = [...sums.entries()]

		entries.forEach(([sum, pickedIndices]) => {
			const nextSum = sum + booking.removablePoints
			if (nextSum > targetPoints) return
			if (sums.has(nextSum)) return

			sums.set(nextSum, [...pickedIndices, index])
		})
	})

	return sums.get(targetPoints) || null
}

async function buildTransferPlan({ sourceClientId, targetBookingReference, readDoc, readQuery }) {
	const cleanedSourceClientId = sourceClientId.trim()
	const cleanedTargetReference = String(targetBookingReference || "").trim()

	if (!cleanedSourceClientId) {
		throw new Error("Please enter a source client ID.")
	}

	if (!cleanedTargetReference) {
		throw new Error("Please enter a target booking reference.")
	}

	const sourceClientRef = db.doc(`clients/${cleanedSourceClientId}`)
	const sourceClientDoc = await readDoc(sourceClientRef)

	if (!sourceClientDoc.exists) {
		throw new Error("Source client was not found.")
	}

	const sourceClientData = sourceClientDoc.data()
	const sourceClientName = `${sourceClientData?.first_name || ""} ${sourceClientData?.last_name || ""}`.trim() || cleanedSourceClientId

	const targetBookingQueries = [db.collection("bookings").where("reference", "==", cleanedTargetReference)]
	if (isValidNumberReference(cleanedTargetReference)) {
		targetBookingQueries.push(db.collection("bookings").where("reference", "==", Number(cleanedTargetReference)))
	}

	const targetBookingSnapshots = await Promise.all(targetBookingQueries.map((query) => readQuery(query)))
	const targetBookingDocs = dedupeBookingDocs(targetBookingSnapshots.flatMap((snapshot) => snapshot.docs))

	if (targetBookingDocs.length === 0) {
		throw new Error("No target booking was found for that reference.")
	}

	if (targetBookingDocs.length > 1) {
		throw new Error("More than one booking matches this reference. Please use a unique reference.")
	}

	const targetBookingDoc = targetBookingDocs[0]
	const targetBookingData = targetBookingDoc.data()
	const targetBookingReferenceValue = targetBookingData?.reference || cleanedTargetReference

	if (targetBookingData?.client === cleanedSourceClientId) {
		throw new Error("Source client matches the target booking client. Use the existing same-client Stableford flow.")
	}

	// Pull the receiving client's name purely for the preview display.
	let targetClientName = ""
	if (targetBookingData?.client) {
		const targetClientDoc = await readDoc(db.doc(`clients/${targetBookingData.client}`))
		if (targetClientDoc.exists) {
			const targetClientData = targetClientDoc.data()
			targetClientName = `${targetClientData?.first_name || ""} ${targetClientData?.last_name || ""}`.trim()
		}
	}

	// Pull all source client bookings and apply strict "already travelled" checks locally.
	const startOfTodayMillis = moment().startOf("day").valueOf()
	const sourceBookingsQuery = db.collection("bookings").where("client", "==", cleanedSourceClientId)
	const sourceBookingsSnapshot = await readQuery(sourceBookingsQuery)
	const sourceBookingDocs = sourceBookingsSnapshot.docs
		.map((doc) => {
			const data = doc.data()
			const travelStartMillis = getTravelStartMillis(data)
			const hasTravelled = Number.isFinite(travelStartMillis) && travelStartMillis < startOfTodayMillis
			const stablefordEarned = Number(data?.stableford?.earned ?? data?.stableford?.amount ?? 0)
			const removablePoints = Math.floor(stablefordEarned)
			const isEligible = hasTravelled && Number(data?.balance_outstanding || 0) <= 0 && removablePoints > 0

			return {
				id: doc.id,
				ref: doc.ref,
				reference: data?.reference || "N/A",
				travelStartMillis,
				agentNotes: data?.agent_notes || "",
				stablefordEarned,
				removablePoints,
				isEligible,
			}
		})
		.filter((booking) => booking.isEligible)

	const pointsEarned = sourceBookingDocs.reduce((total, booking) => total + booking.removablePoints, 0)

	const pointsLedgerQuery = db.collection(`clients/${cleanedSourceClientId}/points_ledger`).where("type", "==", "SPENT")
	const pointsLedgerSnapshot = await readQuery(pointsLedgerQuery)
	const pointsSpent = pointsLedgerSnapshot.docs.reduce((total, doc) => {
		// Voided entries (a partner redemption reversed by staff) no longer hold
		// the points down, so they must not count as spent.
		if (doc.data()?.voided === true) return total

		return total + Number(doc.data()?.points || 0)
	}, 0)
	const transferPoints = pointsEarned - pointsSpent

	if (transferPoints <= 0) {
		throw new Error("No transferable Stableford balance is available for this client.")
	}

	const sortedSourceBookings = [...sourceBookingDocs].sort((a, b) => {
		if (a.travelStartMillis === b.travelStartMillis) return a.id.localeCompare(b.id)
		return a.travelStartMillis - b.travelStartMillis
	})

	const noteText = `Stableford points were removed and used as payment on booking ref: ${targetBookingReferenceValue} instead, as per client request.`
	const pickedIndices = findSubsetForTarget(sortedSourceBookings, transferPoints)
	if (!pickedIndices) {
		throw new Error("Unable to match transferable points to whole source bookings that can be zeroed.")
	}
	const sourceAdjustments = pickedIndices.map((pickedIndex) => {
		const booking = sortedSourceBookings[pickedIndex]
		const nextAgentNotes = appendBookingNote(booking.agentNotes, noteText)

		return {
			bookingId: booking.id,
			bookingReference: booking.reference,
			bookingRef: booking.ref,
			currentStablefordAmount: booking.stablefordEarned,
			pointsRemoved: booking.removablePoints,
			nextStablefordAmount: 0,
			nextAgentNotes,
		}
	})

	const transferAmountGBP = Number((transferPoints / 10).toFixed(2))
	const movedFromReferences = sourceAdjustments.map((adjustment) => adjustment.bookingReference).join(", ")
	const paymentReference = `Points moved from ${sourceClientName} (refs: ${movedFromReferences})`
	const paymentPayee = `${sourceClientName} (${cleanedSourceClientId})`

	const sourceLogMessage = `Stableford points removed and moved to booking ref ${targetBookingReferenceValue} as payment, as per client request.`
	const targetLogMessage = `Stableford payment added from ${sourceClientName} (${cleanedSourceClientId}) for ${transferPoints} points (${formatCurrency(transferAmountGBP)}). Points moved from booking refs: ${movedFromReferences}.`

	const signaturePayload = {
		sourceClientId: cleanedSourceClientId,
		targetBookingId: targetBookingDoc.id,
		targetBookingReference: String(targetBookingReferenceValue),
		targetClientId: targetBookingData?.client || null,
		transferPoints,
		transferAmountGBP,
		sourceAdjustments: sourceAdjustments.map((adjustment) => ({
			bookingId: adjustment.bookingId,
			currentStablefordAmount: adjustment.currentStablefordAmount,
			pointsRemoved: adjustment.pointsRemoved,
			nextStablefordAmount: adjustment.nextStablefordAmount,
			nextAgentNotes: adjustment.nextAgentNotes,
		})),
		paymentReference,
		paymentPayee,
		noteText,
	}

	const previewHash = hashString(JSON.stringify(signaturePayload))

	return {
		previewHash,
		sourceClient: {
			id: cleanedSourceClientId,
			name: sourceClientName,
		},
		targetBooking: {
			id: targetBookingDoc.id,
			reference: targetBookingReferenceValue,
			clientId: targetBookingData?.client || "",
			clientName: targetClientName,
		},
		pointsEarned,
		pointsSpent,
		transferPoints,
		transferAmountGBP,
		sourceAdjustments,
		noteText,
		payment: {
			amount: transferAmountGBP,
			reference: paymentReference,
			payee: paymentPayee,
		},
		sourceLogMessage,
		targetLogMessage,
	}
}

export default function StablefordTransfer() {
	const [sourceClientId, setSourceClientId] = useState("")
	const [targetBookingReference, setTargetBookingReference] = useState("")
	const [previewing, setPreviewing] = useState(false)
	const [executing, setExecuting] = useState(false)
	const [preview, setPreview] = useState(null)
	const [previewInputKey, setPreviewInputKey] = useState("")

	const { pushAlert } = useContext(AlertsContext)
	const { user } = useContext(AuthContext)

	const userUID = auth.currentUser?.uid || ""
	const canAccess = user?.is_super_admin && isSuperAdminAllowlisted(userUID)
	const currentInputKey = `${sourceClientId.trim()}::${String(targetBookingReference || "").trim()}`
	const previewIsCurrent = preview && previewInputKey === currentInputKey

	const sourceRows = useMemo(() => preview?.sourceAdjustments || [], [preview])

	const resetPreview = () => {
		setPreview(null)
		setPreviewInputKey("")
	}

	const updateSourceClientId = (value) => {
		setSourceClientId(value)
		resetPreview()
	}

	const updateTargetBookingReference = (value) => {
		setTargetBookingReference(value)
		resetPreview()
	}

	const resetAll = () => {
		setSourceClientId("")
		setTargetBookingReference("")
		resetPreview()
	}

	const runPreview = async () => {
		try {
			setPreviewing(true)
			const plan = await buildTransferPlan({
				sourceClientId,
				targetBookingReference,
				readDoc: (ref) => ref.get(),
				readQuery: (query) => query.get(),
			})

			setPreview(plan)
			setPreviewInputKey(currentInputKey)

			pushAlert({
				type: "SUCCESS",
				title: "Preview ready",
				body: "Nothing has been written yet. Check the numbers below, then confirm.",
			})
		} catch (error) {
			resetPreview()
			pushAlert({
				type: "ERROR",
				title: "Preview failed",
				body: error.message || "Unable to build a transfer preview.",
			})
		} finally {
			setPreviewing(false)
		}
	}

	const executeTransfer = async () => {
		if (!previewIsCurrent) {
			pushAlert({
				type: "ERROR",
				title: "Preview out of date",
				body: "Please preview again before confirming.",
			})
			return
		}

		try {
			setExecuting(true)

			const plan = await buildTransferPlan({
				sourceClientId,
				targetBookingReference,
				readDoc: (ref) => ref.get(),
				readQuery: (query) => query.get(),
			})

			if (plan.previewHash !== preview.previewHash) {
				throw new Error("The transfer data changed since preview. Please run preview again.")
			}

			const writeCount = plan.sourceAdjustments.length * 2 + 2
			if (writeCount > 500) {
				throw new Error("This transfer affects too many documents for one batch. Please contact development support.")
			}

			const batch = db.batch()

			plan.sourceAdjustments.forEach((adjustment) => {
				batch.update(adjustment.bookingRef, {
					"stableford.amount": adjustment.nextStablefordAmount,
					"stableford.earned": adjustment.nextStablefordAmount,
					agent_notes: adjustment.nextAgentNotes,
				})

				batch.set(db.collection(`bookings/${adjustment.bookingId}/logs`).doc(), {
					type: "STABLEFORD_PAYMENT",
					badge: "INFO",
					message: `${plan.sourceLogMessage} Removed ${adjustment.pointsRemoved} points from this booking.`,
					created: firebase.firestore.FieldValue.serverTimestamp(),
					user: auth.currentUser.uid,
				})
			})

			batch.set(db.collection(`bookings/${plan.targetBooking.id}/payments`).doc(), {
				amount: plan.payment.amount,
				payee: plan.payment.payee,
				reference: plan.payment.reference,
				paid_date: firebase.firestore.FieldValue.serverTimestamp(),
				created: firebase.firestore.FieldValue.serverTimestamp(),
				saved_by: auth.currentUser.uid,
			})

			batch.set(db.collection(`bookings/${plan.targetBooking.id}/logs`).doc(), {
				type: "STABLEFORD_PAYMENT",
				badge: "INFO",
				message: plan.targetLogMessage,
				created: firebase.firestore.FieldValue.serverTimestamp(),
				user: auth.currentUser.uid,
			})

			await batch.commit()

			pushAlert({
				type: "SUCCESS",
				title: "Points moved",
				body: "The payment was written and all source bookings were zeroed.",
			})

			resetPreview()
		} catch (error) {
			resetPreview()
			pushAlert({
				type: "ERROR",
				title: "Move failed",
				body: error.message || "Unable to move the points.",
			})
		} finally {
			setExecuting(false)
		}
	}

	if (user?.loading) return null
	if (!canAccess) return <Redirect to="/clients" />

	return (
		<Tile fullPage={true}>
			<Title>
				<h1>Move Stableford Points</h1>
				<p>Zero a client's Stableford points across their travelled bookings and write the value as a payment onto another client's booking.</p>
			</Title>

			<div className="stableford-transfer">
				<WindowCard
					title="Transfer details"
					subtitle="Enter the client whose points are being moved and the booking reference the payment should be written onto.">
					<div className="input-grid cols-2">
						<Input
							label="Source client ID"
							value={sourceClientId}
							onChange={updateSourceClientId}
							placeholder="Client ID the points are moving from"
						/>

						<Input
							label="Target booking reference"
							value={targetBookingReference}
							onChange={updateTargetBookingReference}
							placeholder="Booking reference the payment is written onto"
						/>
					</div>

					<div className="stableford-transfer__actions">
						<Button
							label="Preview"
							loading={previewing}
							loadingText="Building preview..."
							onClick={() => runPreview()}
							disabled={!sourceClientId.trim() || !String(targetBookingReference || "").trim()}
						/>

						<Button
							label="Reset"
							type="secondary"
							onClick={() => resetAll()}
						/>
					</div>
				</WindowCard>

				{preview && (
					<>
						<WindowCard
							className="has-top-margin"
							title="Preview"
							subtitle="Nothing has been written yet. Check the numbers below before confirming.">
							<div className="stableford-transfer__summary">
								<div className="stableford-transfer__stat">
									<p className="stableford-transfer__stat-label">Moving from</p>
									<p className="stableford-transfer__stat-value">{preview.sourceClient.name}</p>
									<p className="stableford-transfer__stat-meta">{preview.sourceClient.id}</p>
								</div>

								<div className="stableford-transfer__stat">
									<p className="stableford-transfer__stat-label">Paying onto</p>
									<p className="stableford-transfer__stat-value">
										{preview.targetBooking.clientName || "Unknown client"} ({preview.targetBooking.reference})
									</p>
									<p className="stableford-transfer__stat-meta">
										<NavLink to={`/booking/${preview.targetBooking.id}`}>View booking</NavLink>
									</p>
								</div>

								<div className="stableford-transfer__stat">
									<p className="stableford-transfer__stat-label">Points to move</p>
									<p className="stableford-transfer__stat-value">{preview.transferPoints} points</p>
									<p className="stableford-transfer__stat-meta">
										{preview.pointsEarned} earned{preview.pointsSpent > 0 ? `, ${preview.pointsSpent} already spent` : ""}
									</p>
								</div>

								<div className="stableford-transfer__stat">
									<p className="stableford-transfer__stat-label">Payment amount</p>
									<p className="stableford-transfer__stat-value">{formatCurrency(preview.transferAmountGBP)}</p>
									<p className="stableford-transfer__stat-meta">10 points = £1.00</p>
								</div>
							</div>

							<Table
								className="stableford-transfer__table"
								headings={["Booking ref", "Current points", "Points removed", "New points", ""]}
								noResults={sourceRows.length === 0}
								noResultsMessage="No source booking adjustments">
								{sourceRows.map((row) => (
									<tr key={row.bookingId}>
										<td>{row.bookingReference}</td>
										<td>{row.currentStablefordAmount}</td>
										<td>{row.pointsRemoved}</td>
										<td>
											<Badge
												type="POSITIVE"
												label={`${row.nextStablefordAmount}`}
											/>
										</td>
										<td className="is-button align-right">
											<NavLink to={`/booking/${row.bookingId}`}>View booking</NavLink>
										</td>
									</tr>
								))}
							</Table>
						</WindowCard>

						<WindowCard
							className="has-top-margin"
							title="What will be written"
							subtitle="The payment added onto the target booking, plus the note appended to each source booking above.">
							<div className="stableford-transfer__detail-grid">
								<div className="stableford-transfer__detail">
									<p className="stableford-transfer__detail-title">Payment on target booking</p>
									<p>
										<strong>Payee:</strong> {preview.payment.payee}
									</p>
									<p>
										<strong>Reference:</strong> {preview.payment.reference}
									</p>
									<p>
										<strong>Amount:</strong> {formatCurrency(preview.payment.amount)}
									</p>
								</div>

								<div className="stableford-transfer__detail">
									<p className="stableford-transfer__detail-title">Note appended to source bookings</p>
									<p>{preview.noteText}</p>
								</div>
							</div>

							<div className="stableford-transfer__actions">
								<Button
									label="Confirm move"
									loading={executing}
									loadingText="Moving points..."
									onClick={() => executeTransfer()}
									disabled={!previewIsCurrent || previewing}
								/>

								<Button
									label="Cancel"
									type="secondary"
									onClick={() => resetPreview()}
								/>
							</div>
						</WindowCard>
					</>
				)}
			</div>
		</Tile>
	)
}
